import {defineStore} from 'pinia'
import {useApiRequestStore} from '../../ApiRequestStore/useApiRequestStore'
import {
  IDeleteGroupRequestInterfaces,
} from '@toolify/server/src/controllers/GroupController/validators/DeleteGroup/types/IDeleteGroupRequestInterfaces'
import {
  IAddUsersToGroupRequestInterfaces,
} from '@toolify/server/src/controllers/GroupController/validators/AddUsersToGroup/types/IAddUsersToGroupRequestInterfaces'
import {
  IRemoveGroupMembersRequestInterfaces,
} from '@toolify/server/src/controllers/GroupController/validators/RemoveGroupMembers/types/IRemoveGroupMembersRequestInterfaces'
import {
  IRemoveGroupMemberRequestInterfaces,
} from '@toolify/server/src/controllers/GroupController/validators/RemoveGroupMember/types/IRemoveGroupMemberRequestInterfaces'
import {
  IGetGroupMembersRequestInterfaces,
} from '@toolify/server/src/controllers/GroupController/validators/GetGroupMembers/types/IGetGroupMembersRequestInterfaces'
import {
  IGetWorkspaceGroupsRequestInterfaces,
} from '@toolify/server/src/controllers/GroupController/validators/GetWorkspaceGroups/types/IGetWorkspaceGroupsRequestInterfaces'
import {
  ICreateGroupRequestInterfaces,
} from '@toolify/server/src/controllers/GroupController/validators/CreateGroup/types/ICreateGroupRequestInterfaces'
import {
  IUpdateGroupRequestInterfaces,
} from '@toolify/server/src/controllers/GroupController/validators/UpdateGroup/types/IUpdateGroupRequestInterfaces'
import {
  IGetWorkspaceGroupsWithMembersRequestInterfaces,
} from '@toolify/server/src/controllers/GroupController/validators/GetWorkspaceGroupsWithMembers/types/IGetWorkspaceGroupsWithMembersRequestInterfaces'
import {
  IUpdateGroupPrioritiesRequestInterfaces,
} from '@toolify/server/src/controllers/GroupController/validators/UpdateGroupPriorities/types/IUpdateGroupPrioritiesRequestInterfaces'
import {mapUserResponseInstance} from '@toolify/client/src/modules/api/singletons/mapUserResponseInstance'
import {createKeyedRequestCache} from '../keyedRequestCache'
import {IApiStoreRequestOptions} from '@toolify/client/src/stores/ApiRequestStore/types/IApiStoreRequestOptions'

const GROUPS_WITH_MEMBERS_CACHE_TTL_MS = 60 * 1000
const groupsWithMembersCacheByWorkspaceId = createKeyedRequestCache<IGetWorkspaceGroupsWithMembersRequestInterfaces['response']>({ttlMs: GROUPS_WITH_MEMBERS_CACHE_TTL_MS})

export const useGroupApiStore = defineStore('groupApi', {
  actions: {
    async getGroups(
      workspaceId: string,
      options?: IApiStoreRequestOptions,
    ): Promise<IGetWorkspaceGroupsRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.get<IGetWorkspaceGroupsRequestInterfaces>(`/workspace/${workspaceId}/groups`, undefined, options)
    },

    async getGroupsWithMembers(
      workspaceId: string,
    ): Promise<IGetWorkspaceGroupsWithMembersRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const response = await apiRequestStore.get<IGetWorkspaceGroupsWithMembersRequestInterfaces>(`/workspace/${workspaceId}/groups-with-members`)
      return {
        groupsWithMembers: response.groupsWithMembers.map(({group, memberIds}) => {
          return {
            group,
            memberIds,
          }
        }),
      }
    },

    async getCachedGroupsWithMembers(
      workspaceId: string,
    ): Promise<IGetWorkspaceGroupsWithMembersRequestInterfaces['response']> {
      return groupsWithMembersCacheByWorkspaceId.get(workspaceId, () => this.getGroupsWithMembers(workspaceId))
    },

    async createGroup(
      workspaceId: string,
      data: unknown,
    ): Promise<ICreateGroupRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.post<ICreateGroupRequestInterfaces>(`/workspace/${workspaceId}/group`, {
        data,
      })
    },

    async updateGroup(
      workspaceId: string,
      groupId: string,
      data: unknown,
    ): Promise<IUpdateGroupRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.patch<IUpdateGroupRequestInterfaces>(`/workspace/${workspaceId}/group/${groupId}`, {
        data,
      })
    },

    async getGroupMembers(
      workspaceId: string,
      groupId: string,
    ): Promise<IGetGroupMembersRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      const {users} = await apiRequestStore.get<IGetGroupMembersRequestInterfaces>(`/workspace/${workspaceId}/group/${groupId}/users`)
      return {
        users: users.map(user => {
          return mapUserResponseInstance(user)
        }),
      }
    },

    async deleteGroupMember(
      workspaceId: string,
      groupId: string,
      userId: string,
    ): Promise<IRemoveGroupMemberRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.delete<IRemoveGroupMemberRequestInterfaces>(`/workspace/${workspaceId}/group/${groupId}/user/${userId}`)
    },

    async deleteGroup(
      workspaceId: string,
      groupId: string,
    ): Promise<IDeleteGroupRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.delete<IDeleteGroupRequestInterfaces>(`/workspace/${workspaceId}/group/${groupId}`)
    },

    async deleteGroupMembers(
      workspaceId: string,
      groupId: string,
      userIds: string[],
    ): Promise<IRemoveGroupMembersRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.delete<IRemoveGroupMembersRequestInterfaces>(`/workspace/${workspaceId}/group/${groupId}/users`, {
        data: {
          userIds,
        },
      })
    },

    async addMembersToGroup(
      workspaceId: string,
      groupId: string,
      userIds: string[],
      options: Pick<IAddUsersToGroupRequestInterfaces['body'], 'moveFromOtherSeatGroups'> = {},
    ): Promise<IAddUsersToGroupRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.post<IAddUsersToGroupRequestInterfaces>(`/workspace/${workspaceId}/group/${groupId}/users`, {
        data: {
          userIds,
          ...options,
        },
      })
    },

    async updateGroupPriorities(
      workspaceId: string,
      groupIds: string[],
    ): Promise<IUpdateGroupPrioritiesRequestInterfaces['response']> {
      const apiRequestStore = useApiRequestStore()
      return await apiRequestStore.patch<IUpdateGroupPrioritiesRequestInterfaces>(`/workspace/${workspaceId}/groups/priorities`, {
        data: {
          groupIds,
        },
      })
    },
  },
})
